<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CollapsibleColumn from '@js/components/collapsibles/CollapsibleColumn.vue';
import CollapsibleContainer from '@js/components/collapsibles/CollapsibleContainer.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref, watch } from 'vue';
import { object, string } from 'yup';

const { tenant, websiteVariant, defaultBaseUrl, countries, languages } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        websiteVariant: {
            type: Object,
            required: false,
        },
        defaultBaseUrl: {
            type: String,
            required: true,
        },
        countries: {
            type: Array,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
    });

const { route } = useRoute();

const form = reactive({
    name: websiteVariant ? websiteVariant.name : '',
    shortName: websiteVariant ? websiteVariant.short_name : '',
    baseUrl: websiteVariant ? websiteVariant.base_url : '',
    isDefault: websiteVariant ? websiteVariant.is_default : false,
    targets: websiteVariant ? websiteVariant.targets : [],
});

const countryHash = Object.fromEntries(
    countries.map((country) => [country.id, country]),
);

const getCountryLanguages = (countryId) =>
    languages.filter(
        ({ id }) => !!countryHash[countryId]?.languageIds?.includes(id),
    );

const newCountries = computed(() =>
    countries.filter(
        ({ id }) => !form.targets.some(({ countryId }) => countryId === id),
    ),
);

const newTargetCountry = ref(null);

watch(newTargetCountry, (country) => {
    if (country) {
        if (!form.targets.some(({ countryId }) => countryId === country.id)) {
            form.targets.push({
                countryId: country.id,
                languages: [],
            });

            form.targets.sort((x, y) =>
                countryHash[x.countryId].name.localeCompare(
                    countryHash[y.countryId].name,
                ),
            );
        }

        newTargetCountry.value = null;
    }
});

const isSubmitting = ref(false);

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.customer.postWebsiteVariant', {
                tenant: tenant.id,
                ...(websiteVariant
                    ? { websiteVariant: websiteVariant.id }
                    : {}),
            }),
            {
                name: form.name,
                shortName: form.shortName,
                baseUrl: form.baseUrl,
                isDefault: form.isDefault,
                targets: form.targets.map(({ countryId, languages }) => ({
                    countryId,
                    languageIds: languages.map(({ id }) => id),
                })),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.customer.websiteVariants', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.dev.website', {
                    tenant: tenant.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                targets: {
                    title: 'Cibles',
                    contentSlotName: 'targets',
                    forceMount: true,
                },
            }"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.name"
                                label="Nom"
                                placeholder="Nom"
                                field-name="name"
                                field-id="name"
                                type="text"
                                required
                            />
                        </FormGrid>

                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.shortName"
                                label="Nom court"
                                placeholder="Nom court"
                                field-name="shortName"
                                field-id="shortName"
                                type="text"
                            />
                        </FormGrid>

                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.baseUrl"
                                label="URL de base"
                                :legend="`Laissez vide pour utiliser la valeur par défaut ( ${defaultBaseUrl} )`"
                                placeholder="URL de base"
                                field-name="baseUrl"
                                field-id="baseUrl"
                                type="text"
                            />
                        </FormGrid>

                        <FormGrid :columns="2">
                            <SwitchField
                                v-model="form.isDefault"
                                label="Par défaut"
                                field-name="isDefault"
                                field-id="isDefault"
                                :disabled="!!websiteVariant?.is_default"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #targets>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <ComboboxField
                                v-model="newTargetCountry"
                                :options="newCountries"
                                label="Ajouter un pays"
                                field-name="newTargetCountry"
                                field-id="newTargetCountry"
                                filtered-option-key="name"
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>
                        </FormGrid>
                    </FormContent>

                    <CollapsibleColumn>
                        <CollapsibleContainer
                            v-for="(target, targetKey) in form.targets"
                            :key="targetKey"
                            :title="`${countryHash[target.countryId].name} - ${
                                target.languages.length === 0
                                    ? 'Toutes'
                                    : target.languages.length + ' langue(s)'
                            }`"
                            keep-mounted
                        >
                            <ComboboxField
                                v-model="target.languages"
                                :options="getCountryLanguages(target.countryId)"
                                :multiple="true"
                                label="Langues"
                                legend="Laissez vide pour cibler toutes les langues du pays"
                                :field-name="`targetLanguages-${target.countryId}`"
                                :field-id="`targetLanguages-${target.countryId}`"
                                filtered-option-key="name"
                                by="id"
                            >
                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ComboboxField>

                            <div class="flex justify-end">
                                <ActionButton
                                    type="transparent"
                                    :callback="
                                        () => form.targets.splice(targetKey, 1)
                                    "
                                >
                                    Retirer
                                </ActionButton>
                            </div>
                        </CollapsibleContainer>
                    </CollapsibleColumn>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="websiteVariant"
                    :url="
                        route(
                            'front.projectManagement.customer.deleteWebsiteVariant',
                            {
                                tenant: tenant.id,
                                websiteVariant: websiteVariant.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.customer.websiteVariants',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
